import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
} from "@/components/ui/sheet";
import { 
  Store, 
  ShoppingBag, 
  Truck, 
  Package, 
  User, 
  Users,
  Percent, 
  CreditCard, 
  ClipboardList, 
  BarChart3, 
  PieChart, 
  LayoutGrid, 
  ScanBarcode, 
  Settings, 
  ArrowLeft, 
  Tag,
  Tags,
  Menu,
  Palette
} from "lucide-react";

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const navGroups = [
    {
      items: [
        { label: t("admin_nav.dashboard"), href: "/admin", icon: Store },
        { label: t("admin_nav.orders"), href: "/admin/orders", icon: ShoppingBag },
        { label: t("admin_nav.shipping"), href: "/admin/shipping", icon: Truck },
        { label: t("admin_nav.brands"), href: "/admin/brands", icon: Tag },
        { label: "Kategori", href: "/admin/categories", icon: Tags },
        { label: t("admin_nav.products"), href: "/admin/products", icon: Package },
        { label: "Pengaturan PO", href: "/admin/po", icon: ClipboardList },
        { label: t("admin_nav.customers"), href: "/admin/customers", icon: Users },
        { label: t("admin_nav.users"), href: "/admin/users", icon: User },
        { label: t("admin_nav.discounts"), href: "/admin/discounts", icon: Percent },
      ]
    },
    {
      title: t("admin_nav.finance"),
      items: [
        { label: t("admin_nav.payments"), href: "/admin/rekenings", icon: CreditCard },
        { label: t("admin_nav.expense"), href: "/admin/expense", icon: ClipboardList },
      ]
    },
    {
      title: t("admin_nav.tools"),
      items: [
        { label: t("admin_nav.master_variants"), href: "/admin/master-variants", icon: Palette },
        { label: t("admin_nav.reports"), href: "/admin/reports", icon: BarChart3 },
        { label: t("admin_nav.analytics"), href: "/admin/analytics", icon: PieChart },
        { label: t("admin_nav.addons"), href: "/admin/addons", icon: LayoutGrid },
        { label: t("admin_nav.label"), href: "/admin/label", icon: ScanBarcode },
        { label: t("admin_nav.settings"), href: "/admin/settings", icon: Settings },
      ]
    }
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      <div className="p-6 pb-2">
        <Link to="/" onClick={() => setIsOpen(false)} className="font-serif text-xl text-stone-900 block mb-6">Haniyah Gallery</Link>
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-bold">{t("admin_nav.admin_panel")}</p>
        </div>
      </div>
      
      <nav className="flex-1 overflow-y-auto px-4 pb-6 space-y-6 scrollbar-thin scrollbar-thumb-stone-100">
        {navGroups.map((group, gIdx) => (
          <div key={gIdx} className="space-y-1">
            {group.title && (
              <div className="flex items-center justify-between px-3 py-2 mt-2 mb-1">
                <p className="text-[11px] font-bold text-stone-400 uppercase tracking-wider">{group.title}</p>
                <ArrowLeft className="w-3 h-3 text-stone-300 -rotate-90" />
              </div>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 text-sm rounded transition-all duration-200 ${active ? "bg-primary/10 text-primary font-semibold" : "text-stone-600 hover:bg-stone-50"}`}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-stone-400"}`} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-stone-100 bg-white">
        <button onClick={() => { navigate("/"); setIsOpen(false); }} className="flex items-center gap-2 text-[11px] font-bold text-stone-500 hover:text-stone-800 uppercase tracking-wider transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("admin_nav.back_to_store")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-layout">
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="w-64 flex flex-col bg-white border-r border-stone-200 hidden lg:flex">
          <SidebarContent />
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-[#FAF9F6]">
          <div className="p-6 lg:p-10 max-w-7xl mx-auto">
            <div className="lg:hidden flex items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger asChild>
                    <button className="text-stone-900 focus:outline-none">
                      <Menu className="w-6 h-6" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-72">
                    <SidebarContent />
                  </SheetContent>
                </Sheet>
                <Link to="/admin" className="font-serif text-xl text-stone-900">Admin</Link>
              </div>
            </div>
            {title && <h1 className="font-serif text-2xl sm:text-4xl text-stone-900 mb-8" data-testid="admin-page-title">{title}</h1>}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
